import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { FullStory } from '@fullstory/browser';

import { Text } from 'shared/components/Text/Text';
import { VideoPlayer } from 'modules/support/components/VideoCard/VideoPlayer';
import {
  perSessionInstallationCompleteModal,
  perSessionPreInstallationCompleteModal,
  perSessionSystemActivationCompleteModal,
} from 'constants/localStorageKeys';
import { MilestoneType, OnboardingStep } from 'modules/types/util';
import { OnboardingMilestone as OnboardingMilestoneType, VideoResourceType } from 'api/types';
import { useGetVideoResources } from 'api/videoResources';
import useUser from 'hooks/useUser';
import useOnboardingStep from 'hooks/useOnboardingStep';
import { getDashboardRedirectPath } from 'modules/auth/Auth.utils';
import { toast } from 'shared/components/Toast/Toast';
import StatusPill from 'shared/components/StatusPill/StatusPill';
import { LoadingSpinner } from 'shared/components/LoadingSpinner';
import { useDownloadContract, useSetOnboardingMilestone } from 'api/agreement';
import { ArrowRightIcon } from 'shared/components/icons/ArrowRightIcon';
import pages from 'pages';
import Button from 'shared/components/Button/Button';
import {
  OnboardingMilestoneBaseContainer,
  OnboardingMilestoneContainer,
  OnboardingMilestoneContent,
  OnboardingMilestoneContentText,
  OnboardingMilestoneContentTextBody,
  OnboardingMilestoneContentTextHeader,
  OnboardingMilestoneContentVideo,
  OnboardingMilestoneDetails,
  OnboardingMilestoneDetailsCardsWrapper,
  Title,
  ULStyled,
  VideoPlayerWrapper,
} from './OnboardingMilestone.styles';
import { AutoPayStatusCard } from '../../SystemDetails/SystemDetailsCards/AutoPayStatusCard';
import { ContractCard } from '../../SystemDetails/SystemDetailsCards/ContractCard';
import { InstallPartnerCard } from '../../SystemDetails/SystemDetailsCards/InstallPartnerCard';
import { MilestoneCompleteModal } from '../../MilestoneCompleteModal/MilestoneCompleteModal';
import { AutopayModal, AutoPayStep } from '../AutoPay/AutoPayModal/AutopayModal';
import { AutoPayModalSuccess } from '../AutoPay/AutoPayModalSuccess';

enum ACHModalType {
  FORM = 'FORM',
  SUCCESS = 'SUCCESS',
}

const ONBOARDING_STEP_TEXT_KEY_PREFIX: Record<number, string> = {
  [OnboardingStep.PRE_INSTALLATION]: 'onboarding.milestone.preInstallation',
  [OnboardingStep.SYSTEM_INSTALLATION]: 'onboarding.milestone.systemInstallation',
  [OnboardingStep.SYSTEM_ACTIVATION]: 'onboarding.milestone.systemActivation',
};

export function OnboardingMilestone() {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const [downloadErrorModal, setDownloadErrorModal] = useState(false);
  const [achModal, setACHModal] = useState<ACHModalType | null>(null);

  const { userResult, selectedAgreement, refetch: refetchUser } = useUser();
  const { currentStep, goToStep } = useOnboardingStep();

  const { hasDashboardAccess } = getDashboardRedirectPath(userResult?.user, selectedAgreement);

  const { mutateAsync: setOnboardingMilestoneAsync } = useSetOnboardingMilestone();
  const { data: videoResourcesData } = useGetVideoResources();

  const statusText = useMemo(() => {
    if (
      currentStep === OnboardingStep.PRE_INSTALLATION &&
      (selectedAgreement?.current_milestone === MilestoneType.PROJECT_QUALIFICATION_COMPLETED ||
        selectedAgreement?.current_milestone === MilestoneType.INSTALLATION_COMPLETED ||
        selectedAgreement?.current_milestone === MilestoneType.IN_SERVICE)
    ) {
      return t('onboarding.milestone.complete');
    }

    if (
      currentStep === OnboardingStep.SYSTEM_INSTALLATION &&
      (selectedAgreement?.current_milestone === MilestoneType.INSTALLATION_COMPLETED ||
        selectedAgreement?.current_milestone === MilestoneType.IN_SERVICE)
    ) {
      return t('onboarding.milestone.complete');
    }

    if (
      currentStep === OnboardingStep.SYSTEM_ACTIVATION &&
      (!selectedAgreement?.current_milestone ||
        selectedAgreement.current_milestone === MilestoneType.IN_SERVICE)
    ) {
      return t('onboarding.milestone.complete');
    }

    return t('onboarding.milestone.inProgress');
  }, [currentStep, selectedAgreement?.current_milestone, t]);

  const video: VideoResourceType | null = useMemo(() => {
    const videos = videoResourcesData
      ? videoResourcesData.filter(
          (videoItem) =>
            !videoItem.hidden &&
            videoItem.tags.some((tagItem) =>
              tagItem.tag.toLowerCase().includes(OnboardingStep[currentStep].toLowerCase()),
            ),
        )
      : [];

    if (videos.length > 0) return videos[0];

    return null;
  }, [currentStep, videoResourcesData]);

  const { mutate: download } = useDownloadContract({
    onSuccess: (data) => {
      const { presigned_url: presignedUrl } = data;
      const link = document.createElement('a');
      link.href = presignedUrl;
      link.download = 'signed_everbright_contract.pdf';
      link.click();
      link.remove();
    },
    onError: () => {
      toast({
        type: 'error',
        title: t('toast.error'),
        message: t('projectQualification.somethingWentWrong'),
        theme,
      });
    },
  });

  const formattedDate = selectedAgreement?.contract_signed_date
    ? format(new Date(selectedAgreement?.contract_signed_date), 'MMMM d, yyyy')
    : undefined;

  const handleDownload = () => {
    if (selectedAgreement?.contract_resource_id) {
      download(selectedAgreement?.id);
    } else {
      setDownloadErrorModal(true);
    }
  };

  const updateAgreementOnboardingMilestone = async () => {
    if (selectedAgreement && selectedAgreement.onboarding_milestone) {
      let newMilestone: OnboardingMilestoneType = OnboardingMilestoneType.PRE_INSTALLATION;
      if (selectedAgreement.onboarding_milestone === OnboardingMilestoneType.PRE_INSTALLATION) {
        newMilestone = OnboardingMilestoneType.SYSTEM_INSTALLATION;
      }
      if (selectedAgreement.onboarding_milestone === OnboardingMilestoneType.SYSTEM_INSTALLATION) {
        newMilestone = OnboardingMilestoneType.SYSTEM_ACTIVATION;
      }
      if (selectedAgreement.onboarding_milestone === OnboardingMilestoneType.SYSTEM_ACTIVATION) {
        newMilestone = OnboardingMilestoneType.ONBOARDING_COMPLETE;
      }

      await setOnboardingMilestoneAsync({
        agreement_id: selectedAgreement.id,
        body: { milestone: newMilestone },
      });

      refetchUser();
    }
  };

  const handleClickContinue = async () => {
    let nextStep: OnboardingStep | null = OnboardingStep.SYSTEM_INSTALLATION;

    if (currentStep === OnboardingStep.SYSTEM_INSTALLATION) {
      nextStep = OnboardingStep.SYSTEM_ACTIVATION;
    }
    if (currentStep === OnboardingStep.SYSTEM_ACTIVATION) {
      nextStep = null;
    }

    await updateAgreementOnboardingMilestone();
    goToStep(nextStep);
  };

  const handleCompleteACH = () => {
    setACHModal(ACHModalType.SUCCESS);
  };
  const handleCloseACHSuccessModal = () => {
    setACHModal(null);
  };

  const handleOnPlayVideo = () => {
    FullStory('trackEvent', {
      name: `Onboarding - ${OnboardingStep[currentStep]}: - Clicked to play video`,
      properties: {
        page: 'Status',
        onboardingStep: OnboardingStep[currentStep],
        onboardingMilestone: selectedAgreement?.onboarding_milestone,
      },
    });
  };

  const handleClickVideoResources = () => {
    FullStory('trackEvent', {
      name: `Onboarding - ${OnboardingStep[currentStep]}: Clicked Video Resources`,
      properties: {
        page: 'Status',
        onboardingStep: OnboardingStep[currentStep],
        onboardingMilestone: selectedAgreement?.onboarding_milestone,
      },
    });

    navigate(`/${pages.SUPPORT}/${pages.VIDEO_RESOURCES}`);
  };

  const handleOpenACHModal = () => {
    setACHModal(ACHModalType.FORM);

    let name = `Onboarding - ${OnboardingStep[currentStep]}:`;

    if (selectedAgreement?.ach_elected && selectedAgreement?.ach_verified) {
      name += ' clicked Update Bank Info';
    } else if (!selectedAgreement?.ach_elected) {
      name += ' clicked enroll in AutoPay';
    } else if (selectedAgreement?.ach_elected && !selectedAgreement?.ach_verified) {
      name += ' clicked re-enroll in AutoPay';
    }

    FullStory('trackEvent', {
      name,
      properties: {
        page: 'Status',
        onboardingStep: OnboardingStep[currentStep],
        onboardingMilestone: selectedAgreement?.onboarding_milestone,
      },
    });
  };

  const descriptionList: string[] = useMemo(() => {
    const key = `${ONBOARDING_STEP_TEXT_KEY_PREFIX[currentStep]}.description`;
    return t(key, { returnObjects: true }) as string[];
  }, [currentStep, t]);

  const bulletPointsList: string[] = useMemo(() => {
    const key = `${ONBOARDING_STEP_TEXT_KEY_PREFIX[currentStep]}.bulletPoints`;
    return t(key, { returnObjects: true }) as string[];
  }, [currentStep, t]);

  const conclusionList: string[] = useMemo(() => {
    const key = `${ONBOARDING_STEP_TEXT_KEY_PREFIX[currentStep]}.conclusion`;
    return t(key, { returnObjects: true }) as string[];
  }, [currentStep, t]);

  let milestoneCompleteModalKey = '';
  let showMilestoneCompleteModal = false;
  if (currentStep === OnboardingStep.PRE_INSTALLATION) {
    milestoneCompleteModalKey = perSessionPreInstallationCompleteModal;
    showMilestoneCompleteModal =
      selectedAgreement?.onboarding_milestone === OnboardingMilestoneType.PRE_INSTALLATION &&
      selectedAgreement?.current_milestone === MilestoneType.PROJECT_QUALIFICATION_COMPLETED;
  }

  if (currentStep === OnboardingStep.SYSTEM_INSTALLATION) {
    milestoneCompleteModalKey = perSessionInstallationCompleteModal;
    showMilestoneCompleteModal =
      selectedAgreement?.onboarding_milestone === OnboardingMilestoneType.SYSTEM_INSTALLATION &&
      selectedAgreement?.current_milestone === MilestoneType.INSTALLATION_COMPLETED;
  }

  if (currentStep === OnboardingStep.SYSTEM_ACTIVATION) {
    milestoneCompleteModalKey = perSessionSystemActivationCompleteModal;
    showMilestoneCompleteModal =
      selectedAgreement?.onboarding_milestone === OnboardingMilestoneType.SYSTEM_ACTIVATION &&
      hasDashboardAccess;
  }

  return (
    <>
      <OnboardingMilestoneBaseContainer>
        {!selectedAgreement ? (
          <LoadingSpinner />
        ) : (
          <>
            <OnboardingMilestoneContainer>
              <OnboardingMilestoneContent>
                <OnboardingMilestoneContentText>
                  <OnboardingMilestoneContentTextHeader>
                    <Title as="h1">
                      {t(`${ONBOARDING_STEP_TEXT_KEY_PREFIX[currentStep]}.title`)}
                    </Title>
                    <StatusPill text={statusText} bgColor="backgroundSystemInfoSecondary4100" />
                  </OnboardingMilestoneContentTextHeader>
                  <OnboardingMilestoneContentTextBody>
                    {Array.isArray(descriptionList) &&
                      descriptionList.map((item, idx) => {
                        const key = `description-${idx}`;
                        return (
                          <Text key={key} as="p">
                            {item}
                          </Text>
                        );
                      })}

                    <ULStyled>
                      {Array.isArray(bulletPointsList) &&
                        bulletPointsList.map((item, idx) => {
                          const key = `bullet-points-${idx}`;
                          return (
                            <li key={key}>
                              <Text as="p">{item}</Text>
                            </li>
                          );
                        })}
                    </ULStyled>

                    {Array.isArray(conclusionList) &&
                      conclusionList.map((item, idx) => {
                        const key = `conclusion-${idx}`;
                        return (
                          <Text key={key} as="p">
                            {item}
                          </Text>
                        );
                      })}
                  </OnboardingMilestoneContentTextBody>
                </OnboardingMilestoneContentText>
                <OnboardingMilestoneContentVideo>
                  <VideoPlayerWrapper $hideBorder={!!video?.url}>
                    <VideoPlayer url={video?.url} onPlay={handleOnPlayVideo} />
                  </VideoPlayerWrapper>

                  <Button
                    styleVariant="tertiary"
                    label={t('onboarding.milestone.goToVideoResource')}
                    Icon={ArrowRightIcon}
                    iconRight
                    iconBorderAlign
                    iconCenterAlign
                    onClick={handleClickVideoResources}
                  />
                </OnboardingMilestoneContentVideo>
              </OnboardingMilestoneContent>
              <OnboardingMilestoneDetails>
                <OnboardingMilestoneDetailsCardsWrapper>
                  <AutoPayStatusCard
                    currentMilestone={selectedAgreement.current_milestone}
                    achElected={selectedAgreement.ach_elected}
                    achDetails={{
                      verified: !!selectedAgreement.ach_verified,
                      bankName: selectedAgreement.ach_bank_name,
                      accountLastFourDigits: selectedAgreement.ach_account_number_last_four_digits,
                    }}
                    onClick={handleOpenACHModal}
                  />

                  <ContractCard
                    contractType={selectedAgreement.contract_type}
                    contractSignedDate={formattedDate}
                    contractTerm={selectedAgreement.contract_term || 0}
                    onClick={handleDownload}
                    downloadErrorModal={downloadErrorModal}
                    setDownloadErrorModal={setDownloadErrorModal}
                  />

                  {selectedAgreement.organization && (
                    <InstallPartnerCard
                      installer={selectedAgreement.organization.name}
                      installerUrl={selectedAgreement.organization.website_url}
                    />
                  )}
                </OnboardingMilestoneDetailsCardsWrapper>
              </OnboardingMilestoneDetails>
            </OnboardingMilestoneContainer>
            <AutopayModal
              isOpen={achModal === ACHModalType.FORM}
              agreement={selectedAgreement}
              initialStep={
                selectedAgreement.ach_elected
                  ? AutoPayStep.ADD_BANK_ACCOUNT
                  : AutoPayStep.TERMS_AND_CONDITIONS
              }
              onCloseModal={() => {
                setACHModal(null);
              }}
              onCompleteFormModal={handleCompleteACH}
            />

            <AutoPayModalSuccess
              open={achModal === ACHModalType.SUCCESS}
              onContinue={handleCloseACHSuccessModal}
              styleVariant="secondary"
            />
          </>
        )}
      </OnboardingMilestoneBaseContainer>
      <MilestoneCompleteModal
        modalKey={milestoneCompleteModalKey}
        showModal={showMilestoneCompleteModal}
        onClickContinue={handleClickContinue}
        onClose={
          currentStep === OnboardingStep.SYSTEM_ACTIVATION
            ? handleClickContinue
            : updateAgreementOnboardingMilestone
        }
        onboardingStep={currentStep}
      />
    </>
  );
}

export default OnboardingMilestone;
